import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../lib/utils';
import { Card } from '../components/ui/card';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

import { NewProposalDialog } from '../components/proposals/NewProposalDialog';
import { CategorySettingsDialog } from '../components/proposals/CategorySettingsDialog';
import { ViewProposalDialog } from '../components/proposals/ViewProposalDialog';
import { DriveFilePickerDialog } from '../components/proposals/DriveFilePickerDialog';
import { SendForApprovalDialog } from '../components/proposals/SendForApprovalDialog';
import { ConfirmProposalDialog } from '../components/proposals/ConfirmProposalDialog';
import { ProposalsToolbar } from '../components/proposals/ProposalsToolbar';
import { ProposalListView } from '../components/proposals/ProposalListView';
import { ProposalTileView } from '../components/proposals/ProposalTileView';
import { useProposalsSorting } from '../components/proposals/useProposalsSorting';

const EMPTY_FORM = {
  title: '', client_id: '', client_name: '', company_id: '',
  description: '', amount: '', category: '', requirement: '', total_area_sqmt: ''
};

export const ProposalsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('proposalsViewMode') || 'tile');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDriveDialogOpen, setIsDriveDialogOpen] = useState(false);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isCategorySettingsOpen, setIsCategorySettingsOpen] = useState(false);
  const [isViewProposalDialogOpen, setIsViewProposalDialogOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedApprover, setSelectedApprover] = useState('');
  const [driveFiles, setDriveFiles] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [viewingProposal, setViewingProposal] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [proposalSearch, setProposalSearch] = useState('');
  const [filterProposalStatus, setFilterProposalStatus] = useState('all');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);

  const { sortedProposals, sortField, toggleSort } = useProposalsSorting(
    proposals, companies, proposalSearch, filterProposalStatus
  );

  useEffect(() => {
    localStorage.setItem('proposalsViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (searchParams.get('drive_connected') === 'true') {
      toast.success('Google Drive connected successfully');
    }
    const clientId = searchParams.get('client_id');
    const clientName = searchParams.get('client_name');
    const companyId = searchParams.get('company_id');
    if (searchParams.get('new') === 'true') {
      setFormData((prev) => ({
        ...prev,
        client_id: clientId || '',
        client_name: clientName ? decodeURIComponent(clientName) : '',
        company_id: companyId || ''
      }));
      setIsDialogOpen(true);
    }
  }, [searchParams]);

  const fetchProposals = useCallback(async () => {
    try {
      const [proposalsRes, teamRes, clientsRes, companiesRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/proposals`, { withCredentials: true }),
        axios.get(`${API_URL}/team`, { withCredentials: true }),
        axios.get(`${API_URL}/clients`, { withCredentials: true }),
        axios.get(`${API_URL}/companies`, { withCredentials: true }),
        axios.get(`${API_URL}/settings/proposal-categories`, { withCredentials: true })
      ]);
      setProposals(proposalsRes.data);
      setTeamMembers(teamRes.data);
      setClients(clientsRes.data);
      setCompanies(companiesRes.data);
      setCategories(categoriesRes.data.categories || []);
    } catch {
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProposals(); }, [fetchProposals]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/proposals`, {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        total_area_sqmt: formData.total_area_sqmt ? parseFloat(formData.total_area_sqmt) : null,
        scope_area: formData.scope_area || (formData.total_area_sqmt ? `${formData.total_area_sqmt} sq.ft.` : null)
      }, { withCredentials: true });
      toast.success('Proposal created successfully');
      setIsDialogOpen(false);
      setFormData({ ...EMPTY_FORM, scope_area: '' });
      fetchProposals();
    } catch {
      toast.error('Failed to create proposal');
    }
  };

  const handleStatusChange = async (proposalId, newStatus) => {
    const labels = { draft: 'Draft', for_review: 'For Review', sent_to_client: 'Sent to Client', signed: 'Signed', rejected: 'Rejected' };
    try {
      await axios.patch(`${API_URL}/proposals/${proposalId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Proposal status updated to ${labels[newStatus] || newStatus}`);
      fetchProposals();
    } catch {
      toast.error('Failed to update proposal status');
    }
  };

  const handleConvert = (proposalId) => {
    navigate(`/projects?from_proposal=${proposalId}`);
  };

  const handleDeleteProposal = async (proposalId) => {
    if (!window.confirm('Are you sure you want to delete this proposal? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/proposals/${proposalId}`, { withCredentials: true });
      toast.success('Proposal deleted');
      fetchProposals();
    } catch {
      toast.error('Failed to delete proposal');
    }
  };

  const handleAttachDriveFile = async (file) => {
    try {
      await axios.patch(`${API_URL}/proposals/${selectedProposal.proposal_id}`, {
        drive_file_id: file.id,
        drive_file_name: file.name,
        drive_file_link: file.webViewLink
      }, { withCredentials: true });
      toast.success('Document attached from Drive');
      setIsDriveDialogOpen(false);
      fetchProposals();
    } catch {
      toast.error('Failed to attach document');
    }
  };

  const handleUploadProposalPdf = async (proposalId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(
        `${API_URL}/proposals/${proposalId}/upload-document`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`PDF uploaded to Google Drive (${res.data.folder || 'Proposals'})`);
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      await axios.post(`${API_URL}/settings/proposal-categories`, { name: newCategory.trim() }, { withCredentials: true });
      toast.success('Category added');
      setNewCategory('');
      fetchProposals();
    } catch {
      toast.error('Failed to add category');
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    try {
      await axios.delete(`${API_URL}/settings/proposal-categories/${encodeURIComponent(categoryName)}`, { withCredentials: true });
      toast.success('Category deleted');
      fetchProposals();
    } catch {
      toast.error('Failed to delete category');
    }
  };

  const handleViewProposal = (proposal) => {
    setViewingProposal(proposal);
    setIsViewProposalDialogOpen(true);
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="proposals-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Proposals</h1>
          <p className="text-base text-muted-foreground">Create and manage client proposals</p>
        </div>
        <ProposalsToolbar
          search={proposalSearch}
          setSearch={setProposalSearch}
          statusFilter={filterProposalStatus}
          setStatusFilter={setFilterProposalStatus}
          viewMode={viewMode}
          setViewMode={setViewMode}
          isAdmin={isAdmin}
          onOpenCategorySettings={() => setIsCategorySettingsOpen(true)}
        >
          <NewProposalDialog
            open={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            clients={clients}
            companies={companies}
            categories={categories}
          />
        </ProposalsToolbar>
      </div>

      <CategorySettingsDialog
        open={isCategorySettingsOpen}
        onOpenChange={setIsCategorySettingsOpen}
        categories={categories}
        newCategory={newCategory}
        setNewCategory={setNewCategory}
        onAdd={handleAddCategory}
        onDelete={handleDeleteCategory}
      />

      <ViewProposalDialog
        open={isViewProposalDialogOpen}
        onOpenChange={setIsViewProposalDialogOpen}
        proposal={viewingProposal}
        setViewingProposal={setViewingProposal}
        currentUser={user}
        onNavigate={navigate}
        onUploadPdf={handleUploadProposalPdf}
        onRefresh={fetchProposals}
      />

      <DriveFilePickerDialog
        open={isDriveDialogOpen}
        onOpenChange={setIsDriveDialogOpen}
        files={driveFiles}
        onAttach={handleAttachDriveFile}
      />

      <SendForApprovalDialog
        open={isApprovalDialogOpen}
        onOpenChange={setIsApprovalDialogOpen}
        teamMembers={teamMembers}
        selectedApprover={selectedApprover}
        setSelectedApprover={setSelectedApprover}
        onSend={async () => {
          if (!selectedApprover) { toast.error('Please select an approver'); return; }
          try {
            await axios.post(
              `${API_URL}/proposals/${selectedProposal.proposal_id}/send-for-internal-approval`,
              null,
              { params: { approver_id: selectedApprover }, withCredentials: true }
            );
            toast.success('Proposal sent for approval');
            setIsApprovalDialogOpen(false);
            setSelectedApprover('');
            fetchProposals();
          } catch {
            toast.error('Failed to send for approval');
          }
        }}
      />

      <ConfirmProposalDialog
        open={isConfirmDialogOpen}
        onOpenChange={setIsConfirmDialogOpen}
        proposal={selectedProposal}
        onConfirm={async () => {
          try {
            await axios.post(`${API_URL}/proposals/${selectedProposal.proposal_id}/confirm`, {}, { withCredentials: true });
            toast.success('Proposal confirmed');
            setIsConfirmDialogOpen(false);
            fetchProposals();
          } catch {
            toast.error('Failed to confirm proposal');
          }
        }}
      />

      {proposals.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-heading font-semibold mb-2">No proposals yet</h3>
          <p className="text-muted-foreground mb-6">Create your first proposal to get started</p>
        </Card>
      ) : viewMode === 'list' ? (
        <ProposalListView
          proposals={sortedProposals}
          sortField={sortField}
          onToggleSort={toggleSort}
          onView={handleViewProposal}
          onStatusChange={handleStatusChange}
          onConvert={handleConvert}
          onDelete={handleDeleteProposal}
        />
      ) : (
        <ProposalTileView
          proposals={sortedProposals}
          onView={handleViewProposal}
          onStatusChange={handleStatusChange}
          onConvert={handleConvert}
          onDelete={handleDeleteProposal}
          onUploadPdf={handleUploadProposalPdf}
        />
      )}
    </div>
  );
};
